import { code, db } from 'codemash';
import i18next from 'i18next';
import { Config } from '../configs';
import { manageApiUnavailableError, manageFunctionResponse } from './error/errors';
import { mapApiToDataPayment } from './mappers/order';
import { apiActionChooser } from '../utils/apiActionChooser';
import { functionRequest, resourceRequest } from './client/requests';

export const initiatePayment = async ({
  token,
  paymentType,
  orderId,
  subtotal,
  tips,
  items,
  restaurantType,
  restaurantId,
  tableId,
  paymentMethodId,
  paymentAccountId,
  alertController,
  cart,
  note,
  coupon,
  onlyTips,
  discount,
  totalGuests,
  roomNumber,

  isTakeout,
  isDelivery,
  deliveryEmail,
  deliveryPhone,
  deliveryAddress,
  deliveryCity,
  deliveryZipCode,
  noteForDriver,
  isDeliveryNow,
  deliveryDate,
  deliveryId,
  deliveryRestaurantId,

  employeeId,
}) => {
  try {
    let response;
    const formedFunctionData = {
      token,
      subtotal,
      discount,
      tips,
      items,
      paymentType,
      orderId,
      restaurantType,
      restaurantId,
      tableId,
      paymentMethodId,
      paymentAccountId,
      cart,
      note,
      coupon,
      onlyTips,
      totalGuests,
      roomNumber,

      isTakeout,
      isDelivery,
      deliveryEmail,
      deliveryPhone,
      deliveryAddress,
      deliveryCity,
      deliveryZipCode,
      noteForDriver,
      isDeliveryNow,
      deliveryDate,
      deliveryId,
      deliveryRestaurantId,

      employeeId,
      language: i18next.language,
    };

    await apiActionChooser(
      global.localStorage.getItem('v2-company-id'),
      async () => {
        response = await code.executeFunction({
          functionId: Config.DATA.FUNCTIONS.INITIATE_PAYMENT,
          data: formedFunctionData,
        });
      },
      async () => {
        delete formedFunctionData.token;

        response = await functionRequest({
          functionName: 'InitiatePayment',
          data: JSON.stringify(formedFunctionData),
        });
      },
    );

    const managedResponse = manageFunctionResponse({
      response,
      alertController,
    });

    if (managedResponse.isError) {
      if (!managedResponse.handled) {
        if (managedResponse.message === 'no_order') {
          alertController.error({ text: i18next.t('errors.orderNotFound') });
        } else if (managedResponse.message === 'invalid_amount') {
          alertController.error({ text: i18next.t('errors.invalidPayAmount') });
        } else if (managedResponse.message === 'invalid_status') {
          alertController.error({ text: i18next.t('errors.invalidOrderStatus') });
        } else if (managedResponse.message === 'already_has_payments') {
          alertController.error({ text: i18next.t('errors.alreadyHasPayments') });
        } else if (managedResponse.message === 'outside_schedule') {
          alertController.error({ text: i18next.t('errors.orderingOutsideSchedule') });
        } else if (managedResponse.message === 'discount_mismatch') {
          alertController.error({ text: i18next.t('errors.discountHasChanged') });

          return {
            status: 'discountChange',
            actualDiscount: managedResponse.raw ? managedResponse.raw.actualDiscount || 0 : 0,
          };
        } else {
          alertController.error({ text: i18next.t('errors.unknown') });
        }
      }
    } else {
      return {
        status: managedResponse.status,
        paymentLink: managedResponse.paymentLink,
        paymentKey: managedResponse.paymentKey,

        orderId: managedResponse.orderId,
        isCashPayment: managedResponse.isCashPayment,
      };
    }

    return null;
  } catch (e) {
    manageApiUnavailableError(e);

    if (Config.SHOW_LOGS) {
      console.log('Error while trying to initiate payment', e);
    }
  }

  return null;
};

export const getPayment = async ({ paymentId, isCmPaymentId, userId }) => {
  try {
    let response;

    await apiActionChooser(
      global.localStorage.getItem('v2-company-id'),
      async () => {
        response = await db.executeAggregate({
          id: isCmPaymentId
            ? Config.DATA.AGGREGATES.GET_PAYMENT
            : Config.DATA.AGGREGATES.GET_PAYMENT_BY_EXTERNAL,
          collectionName: Config.DATA.COLLECTIONS.PAYMENTS,
          database: Config.CODEMASH.DB_STORAGE_ID,
          tokens: {
            paymentId,
            userId,
          },
        });
      },
      async () => {
        response = await resourceRequest({
          data: {
            id: isCmPaymentId ? 'ClientGetPayment' : 'ClientGetPaymentByExternal',
            tokens: {
              paymentId,
            },
          },
          action: 'aggregate',
        });
      },
    );

    if (response && response.length > 0) {
      return mapApiToDataPayment({ paymentData: response[0] });
    }
  } catch (e) {
    manageApiUnavailableError(e);

    if (Config.SHOW_LOGS) {
      console.log('Error while trying to get table orders', e);
    }
  }

  return null;
};

export const getPaymentInvoice = async ({ paymentId, email, isDownload, alertController }) => {
  try {
    let response;

    await apiActionChooser(
      global.localStorage.getItem('v2-company-id'),
      async () => {
        response = await code.executeFunction({
          functionId: Config.DATA.FUNCTIONS.GET_INVOICE,
          data: {
            paymentId,
            email,
            isDownload,
          },
        });
      },
      async () => {
        response = await functionRequest({
          functionName: 'SendCompletedReceipt',
          data: JSON.stringify({
            paymentId,
            email,
            isDownload,
            language: i18next.language,
          }),
        });
      },
    );

    const managedResponse = manageFunctionResponse({
      response,
      alertController,
    });

    if (managedResponse.isError) {
      if (!managedResponse.handled) {
        if (managedResponse.message === 'no_payment') {
          alertController.error({ text: i18next.t('errors.noPayment') });
        } else if (managedResponse.message === 'no_invoice') {
          alertController.error({ text: i18next.t('errors.noInvoice') });
        } else {
          alertController.error({ text: i18next.t('errors.unknown') });
        }
      }
    } else {
      return {
        emailSent: managedResponse.emailSent,
        downloadLink: managedResponse.downloadLink,
      };
    }

    return null;
  } catch (e) {
    manageApiUnavailableError(e);

    if (Config.SHOW_LOGS) {
      console.log('Error while trying to get invoice', e);
    }
  }

  return null;
};

export const chargeAuthorizedPayment = async ({ token, externalPaymentId, alertController }) => {
  try {
    let response;

    await apiActionChooser(
      global.localStorage.getItem('v2-company-id'),
      async () => {
        response = await code.executeFunction({
          secretKey: token,
          functionId: Config.DATA.FUNCTIONS.CHARGE_AUTHORIZED_PAYMENT,
          data: {
            token,
            externalPaymentId,
          },
        });
      },
      async () => {
        response = await functionRequest({
          functionName: 'ChargeAuthorizedOrder',
          data: JSON.stringify({ externalPaymentId }),
        });
      },
    );

    const managedResponse = manageFunctionResponse({
      response,
      alertController,
    });

    if (managedResponse.isError) {
      if (!managedResponse.handled) {
        if (managedResponse.message === 'no_payment') {
          alertController.error({ text: i18next.t('errors.noPayment') });
        } else if (managedResponse.message === 'bad_status') {
          // ignore
        } else {
          alertController.error({ text: i18next.t('errors.unknown') });
        }
      }
    } else {
      return {
        status: managedResponse.status,
        errorCode: managedResponse.errorCode,
      };
    }

    return null;
  } catch (e) {
    manageApiUnavailableError(e);

    if (Config.SHOW_LOGS) {
      console.log('Error while trying to get invoice', e);
    }
  }

  return null;
};

export const logPageVisit = async ({ path, search, pageKey }) => {
  try {
    await apiActionChooser(
      global.localStorage.getItem('v2-company-id'),
      async () => {},
      async () => {
        await resourceRequest({
          data: {
            path,
            search,
            pageKey,
          },
          action: 'log-visit',
        });
      },
    );
  } catch (e) {
    //
  }

  return null;
};
